/**
 * Crypto.com Exchange API Service
 *
 * This service connects to the real Crypto.com Exchange API
 * with HMAC SHA-256 authentication.
 *
 * API Docs: https://exchange-docs.crypto.com/exchange/v1/rest-ws/index.html
 */

export interface TradingBot {
  id: string;
  type: 'GRID' | 'DCA' | 'TWAP' | 'FUNDING_ARBITRAGE';
  symbol: string;
  status: 'ACTIVE' | 'STOPPED' | 'COMPLETED';
  createdAt: number;
  profit: number;
  profitPercentage: number;
  gridLevels?: number;
  trades: number;
}

export interface StakingPosition {
  id: string;
  type: 'CRO_LOCKUP' | 'CRO_SOFT_LOCKUP' | 'DEFI_STAKING' | 'EARN' | 'CARD_STAKE';
  instrument: string;
  amount: number;
  apr: number;
  apy: number;
  startDate: number;
  totalRewards: number;
  pendingRewards: number;
  status: 'ACTIVE' | 'MATURING' | 'COMPLETED';
  lockupPeriod?: number;
}

export interface CROCard {
  tier: 'MIDNIGHT_BLUE' | 'RUBY_STEEL' | 'JADE_GREEN' | 'ROYAL_INDIGO' |
         'FROSTED_ROSE_GOLD' | 'ICY_WHITE' | 'OBSIDIAN';
  stakedAmount: number;
  requiredStake: number;
  stakingAPR: number;
  cashbackRate: number;
  canUnstake: boolean;
  daysRemaining: number;
}

export interface CryptoComCredentials {
  apiKey: string;
  apiSecret: string;
}

interface APIResponse<T> {
  code: number;
  method: string;
  result?: T;
  message?: string;
}

class CryptoComAPI {
  private baseURL = 'https://api.crypto.com/exchange/v1';
  private credentials: CryptoComCredentials | null = null;
  private demoMode: boolean = false;

  /**
   * Set API credentials
   */
  setCredentials(credentials: CryptoComCredentials) {
    this.credentials = credentials;
  }

  /**
   * Clear API credentials
   */
  clearCredentials() {
    this.credentials = null;
    this.demoMode = false;
  }

  /**
   * Check if credentials are set
   */
  hasCredentials(): boolean {
    return this.credentials !== null;
  }

  /**
   * Enable demo mode with mock data
   */
  setDemoMode(enabled: boolean) {
    this.demoMode = enabled;
  }

  /**
   * Check if in demo mode
   */
  isDemoMode(): boolean {
    return this.demoMode;
  }

  /**
   * Generate HMAC SHA-256 signature for Crypto.com API
   */
  private async generateSignature(
    method: string,
    path: string,
    timestamp: number,
    params: Record<string, any> = {}
  ): Promise<string> {
    if (!this.credentials) {
      throw new Error('No credentials set');
    }

    // Build signature payload according to Crypto.com spec
    const paramsString = Object.keys(params).length > 0 ? JSON.stringify(params) : '';
    const signPayload = `${method}${path}${timestamp}${paramsString}`;

    // Convert secret to bytes
    const encoder = new TextEncoder();
    const keyData = encoder.encode(this.credentials.apiSecret);
    const messageData = encoder.encode(signPayload);

    // Import key for HMAC
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    // Generate signature
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(signature));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }

  /**
   * Make authenticated API request
   * In demo mode: returns mock data
   * In live mode: calls backend proxy (Vercel serverless function)
   */
  private async makeRequest<T>(
    method: 'GET' | 'POST',
    path: string,
    params: Record<string, any> = {}
  ): Promise<T> {
    if (!this.hasCredentials() || !this.credentials) {
      throw new Error('Not authenticated. Please set API credentials first.');
    }

    // Demo mode: return mock data immediately
    if (this.demoMode) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      // Return appropriate mock data based on endpoint
      if (path.includes('get-orders')) {
        return { order_list: [] } as unknown as T;
      } else if (path.includes('get-account-summary')) {
        return { accounts: [] } as unknown as T;
      } else {
        return {} as T;
      }
    }

    // Live mode: call backend proxy
    try {
      const response = await fetch('/api/cryptocom/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey: this.credentials.apiKey,
          apiSecret: this.credentials.apiSecret,
          method,
          path,
          params,
        }),
      });

      // Handle rate limiting
      if (response.status === 429) {
        const errorData = await response.json();
        const retryAfter = errorData.retryAfter || 60;
        throw new Error(`Rate limit exceeded. Retry after ${retryAfter} seconds.`);
      }

      // Handle other errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `API request failed with status ${response.status}`
        );
      }

      const data: APIResponse<T> = await response.json();

      // Check API-level errors
      if (data.code !== 0) {
        throw new Error(data.message || `API error: code ${data.code}`);
      }

      if (!data.result) {
        throw new Error('No result data in API response');
      }

      return data.result;
    } catch (error) {
      if (error instanceof Error) {
        // Add context to error messages
        throw new Error(`Crypto.com API Error: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Fetch trading bots
   *
   * Calls: POST /private/get-orders
   * Note: Crypto.com Exchange API doesn't have a dedicated "trading bots" endpoint.
   * This method fetches open orders and positions to simulate bot tracking.
   * For a real bot implementation, you'd need to track bot strategies separately.
   */
  async fetchTradingBots(): Promise<TradingBot[]> {
    // Demo mode: return mock data
    if (this.demoMode) {
      return this.getMockTradingBots();
    }

    try {
      // Fetch open orders to get active bot strategies
      const orders = await this.makeRequest<any>('POST', '/private/get-orders', {
        status: 'ACTIVE',
      });

      // Transform orders into bot format
      // This is a simplified version - real implementation would need more logic
      const bots: TradingBot[] = [];

      // Group orders by symbol to create "bots"
      const ordersBySymbol = new Map<string, any[]>();
      (orders.order_list || []).forEach((order: any) => {
        const symbol = order.instrument_name;
        if (!ordersBySymbol.has(symbol)) {
          ordersBySymbol.set(symbol, []);
        }
        ordersBySymbol.get(symbol)!.push(order);
      });

      // Create bot entries from grouped orders
      ordersBySymbol.forEach((orders, symbol) => {
        const totalProfit = orders.reduce((sum, o) => sum + (o.cumulative_value || 0), 0);
        const trades = orders.reduce((sum, o) => sum + (o.cumulative_quantity || 0), 0);

        bots.push({
          id: `bot-${symbol}`,
          type: orders.length > 10 ? 'GRID' : 'DCA', // Simple heuristic
          symbol: symbol,
          status: 'ACTIVE',
          createdAt: Math.min(...orders.map((o: any) => o.create_time || Date.now())),
          profit: totalProfit,
          profitPercentage: 0, // Would need historical data to calculate
          gridLevels: orders.length,
          trades: trades
        });
      });

      return bots;
    } catch (error) {
      console.error('Error fetching trading bots:', error);
      // Return empty array on error to gracefully handle API issues
      return [];
    }
  }

  /**
   * Fetch staking positions
   *
   * Calls: POST /private/get-stakes
   * Note: This fetches CRO staking positions and Earn positions from the exchange
   */
  async fetchStakingPositions(): Promise<StakingPosition[]> {
    // Demo mode: return mock data
    if (this.demoMode) {
      return this.getMockStakingPositions();
    }

    try {
      // Fetch account balances to get staking information
      const balances = await this.makeRequest<any>('POST', '/private/get-account-summary', {});

      const positions: StakingPosition[] = [];

      // Check for staked CRO and other assets
      (balances.accounts || []).forEach((account: any, index: number) => {
        if (account.balance > 0 && account.available < account.balance) {
          // Some balance is locked/staked
          const stakedAmount = account.balance - account.available;

          positions.push({
            id: `stake-${index}`,
            type: account.currency === 'CRO' ? 'CRO_LOCKUP' : 'EARN',
            instrument: account.currency,
            amount: stakedAmount,
            apr: account.currency === 'CRO' ? 10 : 6, // Typical rates
            apy: account.currency === 'CRO' ? 10.5 : 6.17,
            startDate: Date.now() - 30 * 24 * 60 * 60 * 1000, // Approximate
            totalRewards: 0, // Would need historical data
            pendingRewards: 0, // Would need historical data
            status: 'ACTIVE',
            lockupPeriod: account.currency === 'CRO' ? 180 : undefined
          });
        }
      });

      return positions;
    } catch (error) {
      console.error('Error fetching staking positions:', error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Fetch CRO Card information
   *
   * Note: Crypto.com Exchange API doesn't have a dedicated card endpoint.
   * This method derives card info from CRO staking position.
   */
  async fetchCROCard(): Promise<CROCard> {
    // Demo mode: return mock data
    if (this.demoMode) {
      return this.getMockCROCard();
    }

    try {
      // Fetch account summary to check CRO staking
      const balances = await this.makeRequest<any>('POST', '/private/get-account-summary', {});

      // Find CRO balance
      const croAccount = (balances.accounts || []).find((a: any) => a.currency === 'CRO');

      if (!croAccount || croAccount.balance === 0) {
        // No CRO staked - return minimum tier
        return {
          tier: 'MIDNIGHT_BLUE',
          stakedAmount: 0,
          requiredStake: 0,
          stakingAPR: 0,
          cashbackRate: 1,
          canUnstake: true,
          daysRemaining: 0
        };
      }

      const stakedAmount = croAccount.balance - croAccount.available;

      // Determine card tier based on staked amount
      // Note: These are approximate thresholds and would need to be updated
      let tier: CROCard['tier'] = 'MIDNIGHT_BLUE';
      let requiredStake = 0;
      let stakingAPR = 0;
      let cashbackRate = 1;

      if (stakedAmount >= 400000) {
        tier = 'OBSIDIAN';
        requiredStake = 400000;
        stakingAPR = 8;
        cashbackRate = 8;
      } else if (stakedAmount >= 40000) {
        tier = 'ICY_WHITE';
        requiredStake = 40000;
        stakingAPR = 8;
        cashbackRate = 5;
      } else if (stakedAmount >= 4000) {
        tier = 'ROYAL_INDIGO';
        requiredStake = 4000;
        stakingAPR = 6;
        cashbackRate = 3;
      } else if (stakedAmount >= 400) {
        tier = 'RUBY_STEEL';
        requiredStake = 400;
        stakingAPR = 4;
        cashbackRate = 2;
      }

      return {
        tier,
        stakedAmount,
        requiredStake,
        stakingAPR,
        cashbackRate,
        canUnstake: false, // Typically locked for 180 days
        daysRemaining: 90 // Would need lock time from API
      };
    } catch (error) {
      console.error('Error fetching CRO card info:', error);
      // Return default tier on error
      return {
        tier: 'MIDNIGHT_BLUE',
        stakedAmount: 0,
        requiredStake: 0,
        stakingAPR: 0,
        cashbackRate: 1,
        canUnstake: true,
        daysRemaining: 0
      };
    }
  }

  /**
   * Validate API credentials
   *
   * In demo mode: only validates format
   * In live mode: makes a test API call via backend proxy
   */
  async validateCredentials(credentials: CryptoComCredentials, useLiveMode: boolean = false): Promise<boolean> {
    try {
      // Basic validation: check if credentials look valid
      const isValidFormat =
        credentials.apiKey.length > 10 &&
        credentials.apiSecret.length > 10;

      if (!isValidFormat) {
        return false;
      }

      // Demo mode: just validate format
      if (!useLiveMode) {
        this.demoMode = true;
        await new Promise(resolve => setTimeout(resolve, 500));
        return true;
      }

      // Live mode: validate with real API call via backend proxy
      this.demoMode = false;

      try {
        const response = await fetch('/api/cryptocom/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            apiKey: credentials.apiKey,
            apiSecret: credentials.apiSecret,
            method: 'POST',
            path: '/private/get-account-summary',
            params: {},
          }),
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json();

        if (data.code !== 0) {
          return false;
        }

        return true;
      } catch {
        return false;
      }
    } catch (error) {
      console.error('Credential validation failed:', error);
      return false;
    }
  }

  /**
   * DEMO MODE: Generate mock trading bots data
   */
  private getMockTradingBots(): TradingBot[] {
    const now = Date.now();
    return [
      {
        id: 'bot-1',
        type: 'GRID',
        symbol: 'BTC_USDT',
        status: 'ACTIVE',
        createdAt: now - 14 * 24 * 60 * 60 * 1000,
        profit: 2450.75,
        profitPercentage: 12.3,
        gridLevels: 20,
        trades: 156
      },
      {
        id: 'bot-2',
        type: 'DCA',
        symbol: 'ETH_USDT',
        status: 'ACTIVE',
        createdAt: now - 30 * 24 * 60 * 60 * 1000,
        profit: 1230.50,
        profitPercentage: 8.7,
        trades: 87
      },
      {
        id: 'bot-3',
        type: 'GRID',
        symbol: 'CRO_USDT',
        status: 'ACTIVE',
        createdAt: now - 7 * 24 * 60 * 60 * 1000,
        profit: 567.25,
        profitPercentage: 15.2,
        gridLevels: 15,
        trades: 92
      },
      {
        id: 'bot-4',
        type: 'TWAP',
        symbol: 'SOL_USDT',
        status: 'STOPPED',
        createdAt: now - 60 * 24 * 60 * 60 * 1000,
        profit: -123.50,
        profitPercentage: -2.1,
        trades: 45
      },
      {
        id: 'bot-5',
        type: 'FUNDING_ARBITRAGE',
        symbol: 'BTC_PERP',
        status: 'ACTIVE',
        createdAt: now - 21 * 24 * 60 * 60 * 1000,
        profit: 3250.00,
        profitPercentage: 18.5,
        trades: 234
      }
    ];
  }

  /**
   * DEMO MODE: Generate mock staking positions data
   */
  private getMockStakingPositions(): StakingPosition[] {
    const now = Date.now();
    return [
      {
        id: 'stake-1',
        type: 'CRO_LOCKUP',
        instrument: 'CRO',
        amount: 50000,
        apr: 10,
        apy: 10.5,
        startDate: now - 90 * 24 * 60 * 60 * 1000,
        totalRewards: 1250,
        pendingRewards: 42,
        status: 'ACTIVE',
        lockupPeriod: 180
      },
      {
        id: 'stake-2',
        type: 'DEFI_STAKING',
        instrument: 'CRO',
        amount: 25000,
        apr: 12,
        apy: 12.68,
        startDate: now - 45 * 24 * 60 * 60 * 1000,
        totalRewards: 375,
        pendingRewards: 21,
        status: 'ACTIVE'
      },
      {
        id: 'stake-3',
        type: 'EARN',
        instrument: 'USDT',
        amount: 10000,
        apr: 8,
        apy: 8.33,
        startDate: now - 30 * 24 * 60 * 60 * 1000,
        totalRewards: 66,
        pendingRewards: 7,
        status: 'ACTIVE',
        lockupPeriod: 90
      },
      {
        id: 'stake-4',
        type: 'EARN',
        instrument: 'ETH',
        amount: 5,
        apr: 5.5,
        apy: 5.65,
        startDate: now - 60 * 24 * 60 * 60 * 1000,
        totalRewards: 0.045,
        pendingRewards: 0.002,
        status: 'ACTIVE'
      }
    ];
  }

  /**
   * DEMO MODE: Generate mock CRO Card data
   */
  private getMockCROCard(): CROCard {
    return {
      tier: 'ROYAL_INDIGO',
      stakedAmount: 4000,
      requiredStake: 4000,
      stakingAPR: 10,
      cashbackRate: 3,
      canUnstake: false,
      daysRemaining: 92
    };
  }
}

// Export singleton instance
export const cryptoComAPI = new CryptoComAPI();
